import { Injectable } from '@angular/core';
import { delay, Observable } from 'rxjs';
import { IApiResponse } from '@app/api/interfaces/common/api-response.interface';
import { IPaginationMeta } from '@app/api/interfaces/common/pagination-meta.interface';
import { MockService } from '@mock/mock.service';
import { IPipe, IPipeNode } from '@app/api/interfaces/pipe.interface';
import { getRandomFloat, getRandomHexColor, getRandomInt, getRandomItem } from '@app/shared/utils/random';
import { PipeDiameter } from '@app/core/enums/pipe-diameter.enum';
import { enumToArray } from '@app/shared/utils/enumerator';
import { PipeMaterial } from '@app/core/enums/pipe-material.enum';
import { pipesByProjectIdMockData, pipesTestProjectMockData } from '@mock/data/pipes-mock.data';
import { IListFilter } from '@app/api/interfaces/common/list-filter';

@Injectable({ providedIn: 'root' })
export class PipesMockService extends MockService {
  public readonly pipes: IPipe[];
  public pipesPositions: IPipeNode[][] = [];

  private id = 1;

  constructor() {
    super();

    if (this.isMockDataGenerated()) {
      this.pipesPositions = this.generatePipesPositions(
        this.dataGeneratedCount.pipes.count,
        getRandomInt(this.dataGeneratedCount.pipes.minSegments, this.dataGeneratedCount.pipes.maxSegments)
      );
      this.pipes = this.generatePipes();
    } else {
      const mock = pipesTestProjectMockData;

      this.pipesPositions = this.buildPipesPositionsFromMock(mock);
      this.pipes = mock;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public getAll(projectId: string, filter: IListFilter): Observable<IApiResponse<IPipe[], IPaginationMeta>> {
    const pipes = this.isMockDataGenerated() ? this.pipes : (pipesByProjectIdMockData[projectId] ?? []);

    return this.mockResponse(
      'PIPES:getALL',
      {
        data: pipes,
        meta: { pagination: { count: pipes.length, total: pipes.length, offset: 0, limit: pipes.length } },
      },
      arguments
    ).pipe(delay(this.requestDelay));
  }

  private generatePipes(): IPipe[] {
    const pipes: IPipe[] = [];

    for (const pipePositions of this.pipesPositions) {
      for (let i = 0; i < pipePositions.length - 1; i++) {
        pipes.push(this.createPipe(pipePositions[i], pipePositions[i + 1]));
      }
    }

    return pipes;
  }

  private createPipe(from: IPipeNode, to: IPipeNode): IPipe {
    return {
      id: 'pipe_gen' + this.id++,
      lineColor: getRandomHexColor(),
      lineOpacity: getRandomFloat(0, 1, 1),
      lineWidth: getRandomInt(1, 10),
      diameter: getRandomItem([PipeDiameter.Diam18, PipeDiameter.Diam25]),
      diameterMm: getRandomInt(18, 100),
      length: this.calculateSegmentLength(from, to),
      material: getRandomItem(enumToArray(PipeMaterial)),
      soundVelocity: getRandomFloat(1500, 2500, 1),
      nodes: [from, to],
    };
  }

  private generatePipesPositions(count: number, countPoints: number) {
    return new Array(count).fill(0).map(() => {
      return this.generatePipePathPositions(countPoints, getRandomFloat(0.004, 0.006, 8));
    });
  }

  private calculateSegmentLength(a: IPipeNode, b: IPipeNode): number {
    const dx = a.lat - b.lat;
    const dy = a.lon - b.lon;
    return Math.sqrt(dx * dx + dy * dy) * 111_000;
  }

  private generatePipePathPositions(count: number, step = 0.004): IPipeNode[] {
    const path: IPipeNode[] = [];

    let lat = this.mapCenter.lat + getRandomFloat(-0.02, 0.02, 6);
    let lon = this.mapCenter.lon + getRandomFloat(-0.02, 0.02, 6);

    path.push({
      id: '' + this.id++,
      lat,
      lon,
    });

    for (let i = 1; i < count; i++) {
      lat += getRandomFloat(-step, step, 6);
      lon += getRandomFloat(-step, step, 6);

      path.push({
        id: '' + this.id++,
        lat,
        lon,
      });
    }

    return path;
  }

  private buildPipesPositionsFromMock(pipesMockData: IPipe[]): IPipeNode[][] {
    const visited = new Set<string>();
    const result: IPipeNode[][] = [];

    for (const pipe of pipesMockData) {
      const nodes = pipe.nodes.filter(n => {
        const key = `${n.id}-${n.lat.toFixed(6)}-${n.lon.toFixed(6)}`;
        if (visited.has(key)) return false;
        visited.add(key);
        return true;
      });

      if (nodes.length >= 2) {
        result.push(nodes);
      }
    }

    return result;
  }
}
